import { displayDollars, getAppPlanInfo, getPlanName, PlanPrivacy, PlanType, SubscriptionType } from "common/app-plans";
import { IconImage } from "common/components/SerializedApp";
import { getNamedIcon } from "common/named-icons";
import { useBuilderTheme } from "hooks/use-builder-theme";
import { GlideFC } from "lib/glide-fc";
import { useCallback } from "react";

import AppPlanFeatures from "./app-plan-features";
import AppPlanHeader from "./app-plan-header";
import { Card } from "./app-plan-panel-styles";

interface Props {
    plan: PlanType;
    privacy: PlanPrivacy;
    subscription: SubscriptionType;
    appAccentColor: string;
    appIcon: IconImage;
    selected: boolean;
    onSelect: (plan: PlanType, privacy: PlanPrivacy) => void;
}

const AppPlanPanel: GlideFC<Props> = p => {
    const { subscription, onSelect, plan, privacy, appAccentColor, appIcon, selected } = p;

    const appPlanInfo = getAppPlanInfo(plan, privacy, subscription);
    const theme = useBuilderTheme();

    const onClick = useCallback(() => {
        onSelect(plan, privacy);
    }, [onSelect, plan, privacy]);

    const isPublicPro = plan === PlanType.Pro && privacy === PlanPrivacy.Public;
    const isPrivatePro = plan === PlanType.Pro && privacy === PlanPrivacy.Private;
    const isAnnual = subscription === SubscriptionType.Annual;

    let baseColor = theme.g400;
    if (isPublicPro) {
        baseColor = theme.b400;
    } else if (isPrivatePro) {
        baseColor = theme.n900;
    }

    const ListPlus = getNamedIcon("listPlus");

    // FIXME: This is hardcoded
    const perUserCost = isPrivatePro ? (
        <div className="per-user">
            <ListPlus fgColor={baseColor} />
            <div className="amount">{isAnnual ? "$1.50" : "$2"}</div>
            <div className="normal muted">per extra user</div>
        </div>
    ) : (
        <></>
    );

    const displayPrice = isPrivatePro ? displayDollars(appPlanInfo.price * 20) : displayDollars(appPlanInfo.price);

    return (
        <Card color={baseColor} selected={selected} onClick={onClick}>
            <div className="inner">
                <AppPlanHeader plan={plan} privacy={privacy} appAccentColor={appAccentColor} appIcon={appIcon} />
                <div className="info">
                    <div className="type">{getPlanName(appPlanInfo.code).short} App</div>
                    <div className="cost">
                        <div className="number">{displayPrice}</div>
                        <div className="description">
                            <div className="units">{appPlanInfo.units}</div>
                            {isAnnual && <div className="units">billed yearly</div>}
                        </div>
                    </div>
                    {perUserCost}
                    <div className="features">
                        <AppPlanFeatures plan={plan} privacy={privacy} baseColor={baseColor} />
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default AppPlanPanel;
